import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useAppSettings } from "@/contexts/AppSettingsContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Lock, Mail, GraduationCap, Sparkles, ArrowRight, Eye, EyeOff } from "lucide-react";

export default function LoginPage() {
  const { login, user } = useAuth();
  const appSettings = useAppSettings();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Login is a fixed dark page — ensure a persisted "light" theme doesn't
    // invert the brand text into an unreadable dark-on-dark state.
    document.documentElement.classList.remove("light");
    document.documentElement.classList.add("dark");
  }, []);

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const r = await login(email, password);
    setSubmitting(false);
    if (!r.ok) setError(r.error);
    else navigate("/", { replace: true });
  };

  const brandName = appSettings.app_name || "CRM UT Mataram";
  const subtitle = appSettings.app_subtitle || "Leads Console";
  const logoUrl = appSettings.logo_url;

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#070707] relative overflow-hidden">
      {/* Ambient glow */}
      <div className="pointer-events-none absolute -top-40 -left-40 w-[480px] h-[480px] rounded-full bg-primary/20 blur-[120px]" />
      <div className="pointer-events-none absolute -bottom-32 right-0 w-[420px] h-[420px] rounded-full bg-[#FF9F0A]/10 blur-[120px]" />

      {/* Left visual — themed illustration */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden border-r border-white/5">
        {/* Layered background — Universitas Terbuka campus mood (graduation/learning theme) */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, rgba(0,122,255,0.18), transparent 55%), radial-gradient(circle at 80% 80%, rgba(255,159,10,0.12), transparent 50%), linear-gradient(135deg, #0A0F1F 0%, #07080C 60%, #0A0F1F 100%)",
          }}
        />
        {/* Decorative learning illustration (SVG composition) */}
        <svg
          viewBox="0 0 800 800"
          aria-hidden="true"
          className="absolute inset-0 w-full h-full opacity-[0.55]"
          preserveAspectRatio="xMidYMid slice"
        >
          <defs>
            <linearGradient id="bookGrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#007AFF" stopOpacity="0.85" />
              <stop offset="100%" stopColor="#FF9F0A" stopOpacity="0.7" />
            </linearGradient>
            <linearGradient id="lineGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="rgba(255,255,255,0.0)" />
              <stop offset="50%" stopColor="rgba(255,255,255,0.35)" />
              <stop offset="100%" stopColor="rgba(255,255,255,0.0)" />
            </linearGradient>
            <pattern id="dotgrid" x="0" y="0" width="34" height="34" patternUnits="userSpaceOnUse">
              <circle cx="1" cy="1" r="1" fill="rgba(255,255,255,0.05)" />
            </pattern>
          </defs>
          <rect width="800" height="800" fill="url(#dotgrid)" />
          {/* Constellation lines */}
          <g stroke="url(#lineGrad)" strokeWidth="1.2">
            <line x1="60" y1="700" x2="720" y2="120" />
            <line x1="20" y1="500" x2="780" y2="320" />
          </g>
          {/* Mortarboard / graduation cap */}
          <g transform="translate(180, 200)">
            <polygon points="220,30 420,90 220,150 20,90" fill="url(#bookGrad)" opacity="0.85" />
            <rect x="80" y="100" width="280" height="80" rx="4" fill="#FFFFFF" opacity="0.06" />
            <line x1="420" y1="90" x2="430" y2="220" stroke="#FF9F0A" strokeWidth="3" />
            <circle cx="430" cy="225" r="8" fill="#FF9F0A" />
          </g>
          {/* Open book */}
          <g transform="translate(140, 480)">
            <path d="M0 60 Q140 0 280 60 L280 240 Q140 180 0 240 Z" fill="url(#bookGrad)" opacity="0.7" />
            <path d="M280 60 Q420 0 560 60 L560 240 Q420 180 280 240 Z" fill="url(#bookGrad)" opacity="0.55" />
            <line x1="280" y1="60" x2="280" y2="240" stroke="#FFFFFF" strokeOpacity="0.4" strokeWidth="2" />
            {/* Lines on pages */}
            {[80, 110, 140, 170].map((y, i) => (
              <g key={i}>
                <line x1="40" y1={y} x2="240" y2={y - 20} stroke="#FFFFFF" strokeOpacity="0.18" strokeWidth="2" />
                <line x1="320" y1={y - 20} x2="520" y2={y} stroke="#FFFFFF" strokeOpacity="0.18" strokeWidth="2" />
              </g>
            ))}
          </g>
          {/* Floating sparkles */}
          <g fill="#FFFFFF">
            <circle cx="640" cy="180" r="2.5" opacity="0.7" />
            <circle cx="700" cy="260" r="1.5" opacity="0.5" />
            <circle cx="80" cy="320" r="2" opacity="0.5" />
            <circle cx="120" cy="200" r="1.5" opacity="0.55" />
            <circle cx="560" cy="640" r="2" opacity="0.6" />
            <circle cx="700" cy="600" r="2.5" opacity="0.7" />
          </g>
        </svg>

        <div className="absolute inset-0 bg-gradient-to-tr from-[#070707] via-[#070707]/40 to-transparent" />

        <div className="relative z-10 p-10 xl:p-14 flex flex-col justify-between w-full">
          {/* Brand mark */}
          <div className="flex items-center gap-3">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={brandName}
                className="w-12 h-12 rounded-xl object-contain bg-white p-1.5 shadow-lg ring-1 ring-white/10"
              />
            ) : (
              <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center font-heading font-bold text-white text-xl shadow-lg">
                {brandName.slice(0, 1)}
              </div>
            )}
            <div>
              <div className="font-heading text-lg font-bold text-white tracking-tight leading-none">
                {brandName}
              </div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-white/50 mt-1.5">
                {subtitle}
              </div>
            </div>
          </div>

          {/* Hero copy */}
          <div className="max-w-md">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-[10px] tracking-[0.25em] uppercase text-white/70 mb-5">
              <Sparkles className="w-3 h-3 text-primary" strokeWidth={2} />
              Admisi Universitas Terbuka
            </div>
            <h1 className="font-heading text-4xl xl:text-5xl font-bold text-white tracking-tight leading-[1.05] mb-5">
              Konversi calon
              <br />
              <span className="bg-gradient-to-r from-primary to-[#FF9F0A] bg-clip-text text-transparent">
                mahasiswa.
              </span>
              <br />
              Lebih cepat.
            </h1>
            <p className="text-sm text-white/60 leading-relaxed max-w-sm">
              Pusat kendali follow-up untuk tim admisi UT Mataram. Lacak, tugaskan, dan tutup setiap prospek dengan presisi.
            </p>

            <div className="mt-8 grid grid-cols-3 gap-3 max-w-sm">
              {[
                { v: "4", l: "Stages" },
                { v: "∞", l: "Custom fields" },
                { v: "1-tap", l: "WhatsApp" },
              ].map((s) => (
                <div
                  key={s.l}
                  className="border border-white/10 px-3 py-2.5 rounded-lg bg-white/[0.03] backdrop-blur-sm transition-all hover:bg-white/[0.06] hover:border-white/20"
                >
                  <div className="font-mono text-xl font-bold text-white">{s.v}</div>
                  <div className="text-[9px] tracking-[0.2em] uppercase text-white/40 mt-0.5">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-1.5 text-white/40 font-mono tracking-wider">
              <GraduationCap className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span>© UT Mataram 2026</span>
            </div>
            <a
              href="/apply"
              target="_blank"
              rel="noreferrer"
              data-testid="public-apply-link"
              className="group inline-flex items-center gap-1 text-primary hover:text-white transition-colors"
            >
              Form pendaftaran
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
            </a>
          </div>
        </div>
      </div>

      {/* Mobile compact header (visible <lg) */}
      <div className="lg:hidden relative z-10 px-6 pt-10 pb-4 flex items-center gap-3">
        {logoUrl ? (
          <img src={logoUrl} alt={brandName} className="w-11 h-11 rounded-xl object-contain bg-white p-1.5 ring-1 ring-white/10" />
        ) : (
          <div className="w-11 h-11 rounded-xl bg-primary flex items-center justify-center font-heading font-bold text-white text-lg">
            {brandName.slice(0, 1)}
          </div>
        )}
        <div>
          <div className="font-heading text-base font-bold text-white tracking-tight">{brandName}</div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-white/50 mt-0.5">{subtitle}</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-5 sm:p-8 lg:p-12 relative z-10">
        <div className="w-full max-w-md">
          {/* Form card */}
          <div
            data-testid="login-card"
            className="relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-7 sm:p-9 shadow-2xl"
          >
            {/* Top decorative line */}
            <div className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent" />

            <div className="mb-6">
              <div className="text-[10px] tracking-[0.3em] uppercase text-primary/80 mb-3 font-mono">
                Sign In
              </div>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight">
                Selamat datang kembali.
              </h2>
              <p className="text-sm text-white/50 mt-2">
                Masuk ke pusat kendali admisi Anda.
              </p>
            </div>

            <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-white/60 mb-2 block font-medium">
                  Email
                </label>
                <div className="relative group">
                  <Mail className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40 group-focus-within:text-primary transition-colors" strokeWidth={1.5} />
                  <Input
                    data-testid="login-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@ut-mataram.ac.id"
                    autoComplete="email"
                    className="pl-10 h-11 bg-black/30 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-primary/60 focus-visible:border-primary/40"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-white/60 mb-2 block font-medium">
                  Password
                </label>
                <div className="relative group">
                  <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40 group-focus-within:text-primary transition-colors" strokeWidth={1.5} />
                  <Input
                    data-testid="login-password-input"
                    type={showPass ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    className="pl-10 pr-10 h-11 bg-black/30 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-primary/60 focus-visible:border-primary/40"
                    required
                  />
                  <button
                    type="button"
                    data-testid="toggle-password-visibility"
                    onClick={() => setShowPass((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"
                    tabIndex={-1}
                  >
                    {showPass ? <EyeOff className="w-4 h-4" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
                  </button>
                </div>
              </div>

              {error && (
                <div
                  data-testid="login-error"
                  className="text-sm text-[#FF453A] bg-[#FF453A]/10 border border-[#FF453A]/30 rounded-md p-3 animate-in fade-in slide-in-from-top-1"
                >
                  {error}
                </div>
              )}

              <Button
                data-testid="login-submit-button"
                type="submit"
                disabled={submitting}
                className="w-full h-11 bg-gradient-to-r from-primary to-[#0056B3] hover:from-[#0056B3] hover:to-primary text-white font-semibold tracking-wide shadow-lg shadow-primary/20 transition-all group"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    Masuk
                    <ArrowRight className="w-4 h-4 ml-1.5 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
                  </>
                )}
              </Button>
            </form>

            {/* Public form link inside card */}
            <div className="mt-6 pt-5 border-t border-white/10 flex items-center justify-between text-xs">
              <span className="text-white/40">Calon mahasiswa?</span>
              <a
                href="/apply"
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:text-white transition-colors inline-flex items-center gap-1"
              >
                Daftar di sini
                <ArrowRight className="w-3 h-3" strokeWidth={2} />
              </a>
            </div>
          </div>

          {/* Mobile copyright */}
          <div className="lg:hidden mt-6 flex items-center justify-center gap-1.5 text-white/30 text-[11px] font-mono tracking-wider">
            <GraduationCap className="w-3.5 h-3.5" strokeWidth={1.5} />
            <span>© UT Mataram 2026</span>
          </div>
        </div>
      </div>
    </div>
  );
}
